<template>
  <div class="row date-time-select">
    <DatePicker
      class="small-2 medium-2 large-2 columns"
      :class="highlightClass"
      :disabledPicker="disabled"
      :format="displayDateFormat"
      :disabled="disabledDatesForDatePicker"
      :required="required"
      :clearButton="showClearBtn"
      :canPutTotop="canPutTotop"
      :calendarButton="true"
      calendarButtonIcon="fa fa-calendar"
      :value="formattedDate[0]"
      :dirtyCheckNode="dirtyCheckNode"
      :dirtyCheck="dirtyCheck"
      @selected="val => updateDate(val, selectedTime)"
      @cleared="updateDate(null, null)"
    />
    <HKLSelect
      :disabled="disabled"
      class="small-2 medium-2 large-2 columns"
      :class="highlightClass"
      style="width: 140px; min-width: 140px; vertical-align: bottom;"
      :value="formattedDate[1] || undefined"
      :options="timeHklOptions"
      :showSearch="true"
      :required="required"
      placeholder=""
      @change="val => updateDate(selectedDate, val)"
    />
  </div>
</template>

<script>
import { timeOptions } from '@/utils/consts'
import dateUtil from '@/utils/dateUtil'
import datePickerUtil from '@/utils/datePicker'
import DatePicker from '@/controls/DatePicker'
import HKLSelect from '@/controls/Select/HKLSelect.vue'
import lodash from 'lodash'

export default {
  components: {
    HKLSelect,
    DatePicker,
  },
  props: {
    /**
     * required MANDATORY validation for the date & time if true
     */
    required: {
      type: Boolean,
      default: false,
    },
    /**
     * original value of the date & time
     */
    date: {
      type: [String, Number],
      default: null,
    },
    /**
     * original dateTime format of the `date`, default: 'DD MMM YY - HH:mm'
     */
    format: {
      type: String,
      default: 'DD MMM YY - HH:mm',
    },
    /**
     * delimiter used to parse the original `date` by `format`, default: '-'
     */
    dateTimeDelimiter: {
      type: String,
      default: '-',
    },
    /**
     * display date format, default: 'DD MMM YY'
     */
    displayDateFormat: {
      type: String,
      default: 'DD MMM YY',
    },
    /**
     * display time format, default: 'HH:mm'
     */
    displayTimeFormat: {
      type: String,
      default: 'HH:mm',
    },
    /**
     * readonly if true
     */
    disabled: {
      type: Boolean,
      default: false,
    },
    /**
     * disabled dates for selection, e.g. not allow to select days from yesterday to tomorrow , { from: `<date of yesterday>`, to: `<date of tomorrow>` }
     */
    disabledDates: {
      type: Object,
      default: () => {},
    },
    disabledDateTimes: {
      type: Object,
      default: () => {},
    },
    /**
     * drop-down list of time selection, MANDATORY.
     */
    timeOptions: {
      type: Array,
      default: () => timeOptions.slice(1),
    },
    showClearBtn: {
      type: Boolean,
      default: false,
    },
    canPutTotop: {
      type: Boolean,
      default: true,
    },
    highlightClass: {
      type: String,
      default: '',
    },
    isClosingTime: {
      type: Boolean,
      default: false,
    },
    setMinTime: {
      type: Boolean,
      default: false,
    },
    dirtyCheckNode: String,
    dirtyCheck: {
      type: Boolean,
      default: false,
    },
  },

  data () {
    return {
      formattedDate: [null, null],
    }
  },
  computed: {
    useDisableDateTimes () {
      return !lodash.isEmpty(this.disabledDateTimes)
    },
    disabledDatesForDatePicker () {
      return this.useDisableDateTimes ? this.expandDisabledDateTimesToDates() : this.disabledDates
    },
    selectedDate () {
      return this.formattedDate[0]
    },
    selectedTime () {
      return this.formattedDate[1]
    },
    timeHklOptions () {
      return this.filteredTimeOptions.map((time) => ({ name: time, value: time }))
    },
    filteredTimeOptions () {
      return this.useDisableDateTimes ? this.getFilteredTimeOptions(
        this.disabledDateTimes && this.disabledDateTimes.to,
        this.disabledDateTimes && this.disabledDateTimes.from,
      ) : this.getFilteredTimeOptions(
        this.disabledDates && this.disabledDates.to && new Date(new Date(this.disabledDates.to).getTime() + dateUtil.ONE_DAY_MILLI),
        this.disabledDates && this.disabledDates.from,
      )
    },
  },
  watch: {
    date () {
      this.setValue(this.date)
    },
  },
  mounted () {
    this.init()
  },
  methods: {
    updateDate (selectedDate, selectedTime) {
      this.formattedDate = [selectedDate, selectedTime]

      if (selectedDate && selectedTime) {
        const date = dateUtil.parse(selectedDate)
        if (this.isClosingTime) {
          dateUtil.addClosingTime(date, selectedTime)
        } else {
          const timeArray = selectedTime.split(':')
          if (timeArray.length === 2) {
            date.add(timeArray[0].trim(), 'hours')
            date.add(timeArray[1].trim(), 'minutes')
          }
        }
        this.$emit('updateDate', date.valueOf())
        return
      }

      if (!selectedDate && !selectedTime) {
        this.$emit('updateDate', null)
        return
      }
      if ((!selectedDate && selectedTime) || (selectedDate && !selectedTime)) {
        this.$emit('update:partialSelected', this.formattedDate)
      }
    },
    setValue (date) {
      if (date) {
        const parsedDate = dateUtil.toMoment(date)
        this.formattedDate = dateUtil.parseClosingDateTime(parsedDate, this.format, this.dateTimeDelimiter, this.isClosingTime)
      } else {
        this.formattedDate = [null, null]
      }
    },

    init () {
      this.setValue(this.date)
    },
    expandDisabledDateTimesToDates () {
      return {
        to: this.disabledDateTimes && this.disabledDateTimes.to ? new Date(dateUtil.getStartOfDay(this.disabledDateTimes.to) - 1) : null,
        from: this.disabledDateTimes.from,
      }
    },
    getFilteredTimeOptions (to, from) {
      let options = lodash.clone(this.timeOptions)
      const selected = new Date(dateUtil.parse(this.selectedDate).valueOf())

      const disabledFrom = from && new Date(from)
      const isSelectDisabledEnd = disabledFrom && datePickerUtil.dayDiff(selected, disabledFrom) === 0
      if (isSelectDisabledEnd) {
        const timeString = disabledFrom.toTimeString()
        options = options.filter(time => time <= timeString)
      }

      const disabledTo = to && new Date(to)
      const isSelectDisabledStart = disabledTo && datePickerUtil.dayDiff(selected, disabledTo) === 0
      if (isSelectDisabledStart) {
        const timeString = disabledTo.toTimeString()
        options = options.filter(time => time >= timeString)
      }

      return options
    },
  },
}
</script>

<style lang="scss">
.date-time-select {
  .vdp-datepicker {
    margin-right: 10px!important;
    min-width: 140px;
  }
  .hkl-select {
    min-width: 140px;
    vertical-align: bottom;
  }
}
</style>
<style lang="scss" scoped>
html[lang='zh-CN'] {
  .date-time-select {
    .vdp-datepicker {
      min-width: 160px;
    }
  }
}
</style>
